/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse } from 'react-bootstrap';
import { PricingDetail, PriceToggle } from 'wsm-pricing-display';
import { useLabels } from 'wsm-common-data';

// wsm-pricing-display's `layout` prop selects the presentation STYLE, not a
// page. It only accepts 'SRP' (compact card layout) or 'VDP' (detail-page
// layout); the compact style is what we want for rec-vehicles cards on every
// page the widget appears on.
const COMPACT_CARD_LAYOUT = 'SRP';

/**
 * Renders the vehicle-card price presentation for the full-price-stack feature.
 * Owns the collapsed expand/collapse state so it can be unit-tested in
 * isolation. When the full stack isn't shown (final mode / Navigation / flag
 * off / no stack available) it renders the provided finalPriceMarkup.
 */
const PriceSection = ({
	isFullStack,
	priceMode,
	pricing,
	isDisplayingEPrice,
	accountId,
	vehicleAccountId,
	condition,
	uuid,
	vin,
	finalPriceMarkup
}) => {
	const labels = useLabels();
	const [showExpanded, setShowExpanded] = useState(false);

	if (!isFullStack) {
		return finalPriceMarkup;
	}

	const priceStack = (
		<PricingDetail
			accountId={accountId}
			condition={condition}
			isDisplayingEPrice={isDisplayingEPrice}
			isGridLayout={false}
			layout={COMPACT_CARD_LAYOUT}
			pricing={pricing}
			vehicleAccountId={vehicleAccountId}
			vehicleId={uuid}
			vehicleData={{ condition, uuid, vin }}
		/>
	);

	// `full` mode shows the stack directly. `collapsed` mode shows a compact
	// price that expands to the stack, with a Hide Pricing toggle to collapse
	// again — each in its own Collapse so they slide in opposite directions
	// (the coordinated swap SRP uses).
	let stackContent = priceStack;
	if (priceMode === 'collapsed') {
		stackContent = (
			<>
				<Collapse in={!showExpanded}>
					<div>
						<PriceToggle
							account={{ address: {} }}
							condition={condition}
							isGridLayout={false}
							pricing={pricing}
							isDisplayingEPrice={isDisplayingEPrice}
							onToggle={() => setShowExpanded(true)}
						/>
					</div>
				</Collapse>
				<Collapse in={showExpanded}>
					<div>
						{priceStack}
						<div className="hide-pricing-container">
							<button
								type="button"
								className="hide-pricing ddc-font-size-xsmall font-weight-bold text-muted"
								onClick={() => setShowExpanded(false)}
								data-testid="hidePricing"
							>
								{labels.get('HIDE_PRICING') || 'Hide Pricing'}
							</button>
						</div>
					</div>
				</Collapse>
			</>
		);
	}

	return (
		// Stop clicks inside the price stack (the collapsed toggle, dealer
		// options/fees, disclaimers) from bubbling up to the card's
		// navigate-to-VDP handler, so expanding the stack doesn't also send the
		// shopper to the VDP.
		<div
			className="vehicle-price-stack"
			data-testid="priceStack"
			onClick={(e) => e.stopPropagation()}
		>
			{stackContent}
		</div>
	);
};

PriceSection.propTypes = {
	isFullStack: PropTypes.bool,
	priceMode: PropTypes.string,
	pricing: PropTypes.shape(),
	isDisplayingEPrice: PropTypes.bool,
	accountId: PropTypes.string,
	vehicleAccountId: PropTypes.string,
	condition: PropTypes.string,
	uuid: PropTypes.string,
	vin: PropTypes.string,
	finalPriceMarkup: PropTypes.node
};

PriceSection.defaultProps = {
	isFullStack: false,
	priceMode: 'final',
	pricing: {},
	isDisplayingEPrice: false,
	accountId: '',
	vehicleAccountId: '',
	condition: '',
	uuid: '',
	vin: '',
	finalPriceMarkup: null
};

export default PriceSection;
